import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import type { Campaign, UnsubscribedPhoneRow } from '../../types';
import './GestaoDescadastro.css';

const REASON_MIN = 8;

/** Atalhos de período (API: today | week | month | year) */
const PERIOD_SEGMENTS: { value: string; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mês' },
  { value: 'year', label: 'Ano' },
];

const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todas as origens' },
  { value: 'link_click', label: 'Clique no link' },
  { value: 'manual_admin', label: 'Manual (admin)' },
  { value: 'import', label: 'Importação' },
];

const REASON_PRESETS = [
  'Reativação a pedido do cliente, com confirmação verbal.',
  'Opt-out registrado por engano; cliente solicitou voltar a receber mensagens.',
  'Cliente comprovou interesse em campanhas; reativação autorizada pela gestão.',
];

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

/** Case- and accent-insensitive substring match for campaign names */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

const CAMPAIGN_LIST_MAX = 100;

export const GestaoDescadastro: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [items, setItems] = useState<UnsubscribedPhoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [periodMode, setPeriodMode] = useState<string>('today');
  const [source, setSource] = useState<string>('all');
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [campaignId, setCampaignId] = useState<string>('');
  const [campaignQuery, setCampaignQuery] = useState('');
  const [campaignListOpen, setCampaignListOpen] = useState(false);
  const [campaignHighlight, setCampaignHighlight] = useState(0);
  const campaignComboRef = useRef<HTMLDivElement>(null);
  const campaignListId = useId();
  const campaignInputId = useId();
  const periodGroupLabelId = useId();
  const campaignIdRef = useRef(campaignId);
  const campaignQueryRef = useRef(campaignQuery);
  campaignIdRef.current = campaignId;
  campaignQueryRef.current = campaignQuery;

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  const [revokeTarget, setRevokeTarget] = useState<UnsubscribedPhoneRow | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [bulkOkMessage, setBulkOkMessage] = useState<string | null>(null);
  const headerSelectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setQDebounced(q.trim()), 350);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiService.listCampaigns();
        if (!cancelled) setCampaigns(list);
      } catch {
        if (!cancelled) setCampaigns([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCampaigns = useMemo(() => {
    const qf = fold(campaignQuery.trim());
    if (qf.length < 1) return [];
    return [...campaigns]
      .filter((c) => fold(c.nome).includes(qf))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
      .slice(0, CAMPAIGN_LIST_MAX);
  }, [campaigns, campaignQuery]);

  const campaignOptionCount = 1 + filteredCampaigns.length;

  useEffect(() => {
    if (!campaignListOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (campaignComboRef.current && !campaignComboRef.current.contains(e.target as Node)) {
        setCampaignListOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [campaignListOpen]);

  const pickCampaignAll = useCallback(() => {
    setCampaignId('');
    setCampaignQuery('');
    setCampaignListOpen(false);
    setCampaignHighlight(0);
  }, []);

  const pickCampaign = useCallback((c: Campaign) => {
    setCampaignId(c.campaignId);
    setCampaignQuery(c.nome);
    setCampaignListOpen(false);
    setCampaignHighlight(0);
  }, []);

  const onCampaignInputChange = (value: string) => {
    setCampaignQuery(value);
    setCampaignId('');
    setCampaignHighlight(0);
  };

  const onCampaignInputBlur = () => {
    window.setTimeout(() => {
      if (campaignComboRef.current?.contains(document.activeElement)) return;
      if (campaignIdRef.current) return;
      const t = campaignQueryRef.current.trim();
      if (!t) return;
      const exact = campaigns.filter((c) => fold(c.nome) === fold(t));
      if (exact.length === 1) {
        setCampaignId(exact[0].campaignId);
        setCampaignQuery(exact[0].nome);
      }
    }, 180);
  };

  const fetchPage = useCallback(
    async (append: boolean, token?: string) => {
      const res = await apiService.listUnsubscribedPhones({
        period: periodMode,
        source: source === 'all' ? 'all' : source,
        includeRevoked,
        campaignId: campaignId || undefined,
        q: qDebounced || undefined,
        limit: 50,
        nextToken: token,
      });
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
      setNextToken(res.nextPageToken);
    },
    [periodMode, source, includeRevoked, campaignId, qDebounced]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setBulkOkMessage(null);
      try {
        await fetchPage(false, undefined);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Erro ao carregar descadastros');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  useEffect(() => {
    setSelectedIds([]);
    setBulkOkMessage(null);
  }, [periodMode, source, includeRevoked, campaignId, qDebounced]);

  const allVisibleSelected =
    items.length > 0 && items.every((r) => selectedIds.includes(r.unsubscribeId));
  const someVisibleSelected = items.some((r) => selectedIds.includes(r.unsubscribeId));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visible = new Set(items.map((r) => r.unsubscribeId));
      setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
    } else {
      const visible = items.map((r) => r.unsubscribeId);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visible])));
    }
  };

  const handleLoadMore = async () => {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      await fetchPage(true, nextToken);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar mais');
    } finally {
      setLoadingMore(false);
    }
  };

  const openRevoke = (row: UnsubscribedPhoneRow) => {
    setRevokeTarget(row);
    setRevokeReason('');
  };

  const submitRevoke = async () => {
    if (!revokeTarget) return;
    const trimmed = revokeReason.trim();
    if (trimmed.length < REASON_MIN) {
      setError(`Motivo deve ter pelo menos ${REASON_MIN} caracteres.`);
      return;
    }
    setRevokeSubmitting(true);
    setError(null);
    try {
      await apiService.revokeUnsubscribedPhone(revokeTarget.unsubscribeId, trimmed);
      setRevokeTarget(null);
      await fetchPage(false, undefined);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao reativar contato');
    } finally {
      setRevokeSubmitting(false);
    }
  };

  const submitBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleteSubmitting(true);
    setError(null);
    setBulkOkMessage(null);
    const CHUNK = 100;
    let deleted = 0;
    let notFound = 0;
    let forbidden = 0;
    try {
      for (let i = 0; i < selectedIds.length; i += CHUNK) {
        const slice = selectedIds.slice(i, i + CHUNK);
        const res = await apiService.bulkDeleteUnsubscribedPhones(slice);
        deleted += res.deleted?.length ?? 0;
        notFound += res.notFound?.length ?? 0;
        forbidden += res.forbidden?.length ?? 0;
      }
      setDeleteConfirmOpen(false);
      setSelectedIds([]);
      await fetchPage(false, undefined);
      const parts = [`${deleted} registro(s) removido(s) permanentemente.`];
      if (notFound) parts.push(`${notFound} já não existiam.`);
      if (forbidden) parts.push(`${forbidden} não autorizado(s).`);
      if (deleted === 0 && notFound === 0 && forbidden === 0) {
        setError('Nenhum registro foi removido.');
      } else {
        setBulkOkMessage(parts.join(' '));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao excluir registros');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="gestao-desc-page">
      <div className="gestao-desc-wrap">
        <div className="gestao-desc-header">
          <h1>Gestão de descadastro</h1>
          <p>
            Números que solicitaram opt-out (em geral via link). Reativar permite enviar campanhas de novo; o motivo fica
            registrado para auditoria.
          </p>
        </div>

        <div className="gestao-desc-filters">
          <div className="gestao-desc-period-field">
            <span className="gestao-desc-period-label" id={periodGroupLabelId}>
              Período
            </span>
            <div
              className="gestao-desc-period-segment"
              role="group"
              aria-labelledby={periodGroupLabelId}
            >
              {PERIOD_SEGMENTS.map((seg) => (
                <button
                  key={seg.value}
                  type="button"
                  className={`gestao-desc-period-btn${periodMode === seg.value ? ' gestao-desc-period-btn--active' : ''}`}
                  aria-pressed={periodMode === seg.value}
                  onClick={() => setPeriodMode(seg.value)}
                >
                  {seg.label}
                </button>
              ))}
            </div>
          </div>
          <label>
            Origem
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div className="gestao-desc-combobox-wrap" ref={campaignComboRef}>
            <label className="gestao-desc-combobox-label" htmlFor={campaignInputId}>
              Campanha
            </label>
            <div className="gestao-desc-combobox-input-row">
              <input
                id={campaignInputId}
                type="text"
                role="combobox"
                aria-expanded={campaignListOpen}
                aria-controls={campaignListId}
                aria-autocomplete="list"
                className="gestao-desc-combobox-input"
                value={campaignQuery}
                placeholder="Buscar campanha por nome…"
                autoComplete="off"
                onChange={(e) => onCampaignInputChange(e.target.value)}
                onFocus={() => {
                  setCampaignListOpen(true);
                  setCampaignHighlight(0);
                }}
                onBlur={() => onCampaignInputBlur()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setCampaignListOpen(false);
                    return;
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (!campaignListOpen) setCampaignListOpen(true);
                    setCampaignHighlight((h) => Math.min(h + 1, Math.max(0, campaignOptionCount - 1)));
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (!campaignListOpen) setCampaignListOpen(true);
                    setCampaignHighlight((h) => Math.max(0, h - 1));
                    return;
                  }
                  if (e.key === 'Enter' && campaignListOpen) {
                    e.preventDefault();
                    if (campaignHighlight === 0) pickCampaignAll();
                    else {
                      const c = filteredCampaigns[campaignHighlight - 1];
                      if (c) pickCampaign(c);
                    }
                  }
                }}
              />
              {(campaignQuery || campaignId) && (
                <button
                  type="button"
                  className="gestao-desc-combobox-clear"
                  aria-label="Limpar campanha"
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => pickCampaignAll()}
                >
                  ×
                </button>
              )}
              {campaignListOpen && (
                <ul id={campaignListId} className="gestao-desc-combobox-list" role="listbox">
                  <li role="presentation">
                    <button
                      type="button"
                      role="option"
                      className={`gestao-desc-combobox-option${campaignHighlight === 0 ? ' gestao-desc-combobox-option--active' : ''}`}
                      aria-selected={campaignHighlight === 0}
                      onMouseDown={(ev) => ev.preventDefault()}
                      onMouseEnter={() => setCampaignHighlight(0)}
                      onClick={() => pickCampaignAll()}
                    >
                      Todas as campanhas
                    </button>
                  </li>
                  {campaignQuery.trim().length < 1 && (
                    <li className="gestao-desc-combobox-hint" role="presentation">
                      Digite para filtrar campanhas por nome
                    </li>
                  )}
                  {filteredCampaigns.map((c, idx) => {
                    const hi = idx + 1;
                    return (
                      <li key={c.campaignId} role="presentation">
                        <button
                          type="button"
                          role="option"
                          className={`gestao-desc-combobox-option${campaignHighlight === hi ? ' gestao-desc-combobox-option--active' : ''}`}
                          aria-selected={campaignHighlight === hi}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onMouseEnter={() => setCampaignHighlight(hi)}
                          onClick={() => pickCampaign(c)}
                        >
                          {c.nome}
                        </button>
                      </li>
                    );
                  })}
                  {campaignQuery.trim().length >= 1 && filteredCampaigns.length === 0 && (
                    <li className="gestao-desc-combobox-hint" role="presentation">
                      Nenhuma campanha encontrada
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
          <label>
            Busca (nome ou telefone)
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nome ou telefone"
              autoComplete="off"
            />
          </label>
          <label className="gestao-desc-check">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
            />
            Incluir reativados
          </label>
        </div>

        {error && <div className="gestao-desc-error">{error}</div>}
        {bulkOkMessage && <div className="gestao-desc-success">{bulkOkMessage}</div>}

        {loading ? (
          <div className="gestao-desc-loading">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="gestao-desc-empty">Nenhum registro encontrado para os filtros.</div>
        ) : (
          <>
            <div className="gestao-desc-bulk-bar">
              <button type="button" className="gestao-desc-btn" onClick={() => toggleSelectAllVisible()}>
                {allVisibleSelected ? 'Desmarcar todos na lista' : 'Selecionar todos na lista'}
              </button>
              <button
                type="button"
                className="gestao-desc-btn gestao-desc-btn-danger"
                disabled={selectedIds.length === 0}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Excluir selecionados{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </button>
            </div>
            <div className="gestao-desc-table">
              <div className="gestao-desc-row gestao-desc-row-head">
                <span className="gestao-desc-col-check">
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => toggleSelectAllVisible()}
                    aria-label="Selecionar ou desmarcar todos os registros visíveis"
                  />
                </span>
                <span title="Quando o primeiro registo de opt-out foi guardado.">Criado</span>
                <span title="Última alteração (opt-out de novo, reativar, etc.).">Atualizado</span>
                <span>Telefone</span>
                <span>Nome</span>
                <span>Campanha</span>
                <span>Origem</span>
                <span>Status</span>
                <span>Ações</span>
              </div>
              {items.map((row) => (
                <div key={row.unsubscribeId} className="gestao-desc-row">
                  <span className="gestao-desc-col-check">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.unsubscribeId)}
                      onChange={() => toggleSelectRow(row.unsubscribeId)}
                      aria-label={`Selecionar ${row.contactTelefone || row.phoneNormalized}`}
                    />
                  </span>
                  <span title={row.createdAt}>{formatWhen(row.createdAt)}</span>
                  <span title={row.updatedAt || row.createdAt}>
                    {formatWhen(row.updatedAt || row.createdAt)}
                  </span>
                  <span>{row.contactTelefone || row.phoneNormalized}</span>
                  <span>{row.contactNome || '—'}</span>
                  <span title={row.campaignId}>{row.campaignNome || row.campaignId || '—'}</span>
                  <span>{row.source}</span>
                  <span>
                    {row.revokedAt ? (
                      <span className="gestao-desc-badge gestao-desc-badge-revoked">Reativado</span>
                    ) : (
                      <span className="gestao-desc-badge gestao-desc-badge-active">Opt-out</span>
                    )}
                  </span>
                  <span>
                    {!row.revokedAt ? (
                      <button type="button" className="gestao-desc-btn" onClick={() => openRevoke(row)}>
                        Reativar
                      </button>
                    ) : (
                      <span title={row.revokedReason || ''}>—</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            {nextToken && (
              <div className="gestao-desc-more">
                <button
                  type="button"
                  className="gestao-desc-btn gestao-desc-btn-primary"
                  disabled={loadingMore}
                  onClick={() => void handleLoadMore()}
                >
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {deleteConfirmOpen && (
        <div
          className="gestao-desc-modal-overlay"
          role="presentation"
          onClick={() => !deleteSubmitting && setDeleteConfirmOpen(false)}
        >
          <div className="gestao-desc-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Excluir registros permanentemente</h3>
            <p>
              Remover <strong>{selectedIds.length}</strong> registro(s) da base de descadastro (DynamoDB). Esta ação{' '}
              <strong>não pode ser desfeita</strong>. Os números deixam de constar como opt-out e poderão receber
              campanhas de novo (salvo novo opt-out).
            </p>
            <div className="gestao-desc-modal-actions">
              <button
                type="button"
                className="gestao-desc-btn"
                disabled={deleteSubmitting}
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="gestao-desc-btn gestao-desc-btn-danger"
                disabled={deleteSubmitting || selectedIds.length === 0}
                onClick={() => void submitBulkDelete()}
              >
                {deleteSubmitting ? 'Excluindo...' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeTarget && (
        <div className="gestao-desc-modal-overlay" role="presentation" onClick={() => setRevokeTarget(null)}>
          <div className="gestao-desc-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Reativar contato</h3>
            <p>
              <strong>{revokeTarget.contactTelefone || revokeTarget.phoneNormalized}</strong>
              {revokeTarget.contactNome ? ` · ${revokeTarget.contactNome}` : ''}
            </p>
            <p className="gestao-desc-hint">
              O número voltará a poder receber disparos de campanha. Informe o motivo (mín. {REASON_MIN} caracteres).
            </p>
            <div className="gestao-desc-presets">
              {REASON_PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => setRevokeReason(p)} title={p}>
                  {p.length > 36 ? `${p.slice(0, 34)}…` : p}
                </button>
              ))}
            </div>
            <textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder="Ex.: Cliente solicitou por telefone em 02/04/2026."
            />
            <div className="gestao-desc-modal-actions">
              <button type="button" className="gestao-desc-btn" onClick={() => setRevokeTarget(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="gestao-desc-btn gestao-desc-btn-primary"
                disabled={revokeSubmitting || revokeReason.trim().length < REASON_MIN}
                onClick={() => void submitRevoke()}
              >
                {revokeSubmitting ? 'Salvando...' : 'Confirmar reativação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
